import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaTimes, FaPaperPlane, FaLightbulb, FaHistory, FaMinus, FaExpand, FaSpinner } from 'react-icons/fa';
import AIChatService from '../../services/aiChatService';

const AIChatbot = ({ groupId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickSuggestions, setQuickSuggestions] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const messagesEndRef = useRef(null);

  // Initialize when chatbot opens
  useEffect(() => {
    if (isOpen && groupId && !isMinimized) {
      initializeChat();
    }
  }, [isOpen, groupId, isMinimized]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initializeChat = async () => {
    try {
      setConnectionStatus('connecting');
      const data = await AIChatService.startChat(groupId);
      
      if (data.success) {
        setSessionId(data.sessionId);
        setConnectionStatus('connected');
        
        // Clear previous messages and add welcome
        setMessages([
          {
            id: 'welcome',
            role: 'assistant',
            content: data.welcomeMessage,
            timestamp: new Date(),
            fallback: data.fallback || false
          }
        ]);
        
        // Load suggestions
        fetchQuickSuggestions();
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      setConnectionStatus('error');
      
      // Fallback welcome message
      setMessages([
        {
          id: 'fallback-welcome',
          role: 'assistant',
          content: "👋 Hello! I'm TripGenius AI.\n\nI'm here to help with your travel planning. You can ask me about itinerary planning, budget management, or activity suggestions.\n\n*Note: Enhanced AI features are coming soon!*",
          timestamp: new Date(),
          fallback: true
        }
      ]);
    }
  };

  const fetchQuickSuggestions = async () => {
    try {
      const data = await AIChatService.getQuickSuggestions(groupId);
      if (data.success && data.suggestions) {
        setQuickSuggestions(data.suggestions.slice(0, 6));
      }
    } catch (error) {
      console.warn('Could not load suggestions:', error);
      // Use default suggestions
      setQuickSuggestions([
        { id: 'plan', title: '📅 Plan Trip', prompt: 'Help plan our trip itinerary' },
        { id: 'budget', title: '💰 Budget', prompt: 'Help with budget planning' },
        { id: 'activities', title: '🎯 Activities', prompt: 'Suggest fun activities' }
      ]);
    }
  };

  const sendMessage = async (message = inputMessage) => {
    const finalMessage = (message || inputMessage).trim();
    if (!finalMessage) return;

    // Add user message
    const userMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: finalMessage,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const data = await AIChatService.sendMessage(groupId, finalMessage, sessionId);
      
      if (data.success) {
        const aiMessage = {
          id: `ai_${Date.now()}`,
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          fallback: data.fallback || false,
          metadata: data.metadata
        };
        
        setMessages(prev => [...prev, aiMessage]);
        
        if (!sessionId && data.sessionId) {
          setSessionId(data.sessionId);
        }
        
        setConnectionStatus('connected');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Error message
      const errorMessage = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again or check the other dashboard features like expense tracking and activity planning!",
        timestamp: new Date(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setConnectionStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickSuggestionClick = (suggestion) => {
    sendMessage(suggestion.prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    initializeChat();
  };

  const formatMessage = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>')
      .replace(/•/g, '•');
  };

  const ConnectionIndicator = () => {
    if (connectionStatus === 'connected') {
      return <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>;
    } else if (connectionStatus === 'connecting') {
      return <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>;
    } else {
      return <span className="w-2 h-2 bg-red-500 rounded-full"></span>;
    }
  };

  return (
    <>
      {/* Floating Chat Icon */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 animate-bounce"
        title="Ask TripGenius AI - Travel Assistant"
      >
        <FaRobot className="h-7 w-7" />
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-ping"></div>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-end pointer-events-none">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 pointer-events-auto"
            onClick={() => !isMinimized && setIsOpen(false)}
          />
          
          {/* Chat Container */}
          <div className={`relative bg-white rounded-t-2xl lg:rounded-2xl shadow-2xl pointer-events-auto transition-all duration-300 ${isMinimized ? 'h-16 w-72' : 'h-[550px] w-full lg:w-[420px]'} bottom-0 right-0 lg:bottom-6 lg:right-6`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-2xl p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FaRobot className="h-6 w-6" />
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-bold text-lg">TripGenius AI</h3>
                    <ConnectionIndicator />
                  </div>
                  <p className="text-xs opacity-80">Your Travel Assistant</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  title={isMinimized ? "Maximize" : "Minimize"}
                >
                  {isMinimized ? <FaExpand className="h-4 w-4" /> : <FaMinus className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  title="Close"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages Container */}
                <div className="flex-1 overflow-y-auto p-4 h-[calc(100%-180px)] bg-gray-50">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="animate-spin mx-auto mb-4">
                        <FaRobot className="h-12 w-12 text-purple-300" />
                      </div>
                      <p className="text-gray-500">Starting AI assistant...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-2xl p-4 shadow-sm ${msg.role === 'user' ? 'bg-blue-500 text-white rounded-br-none' : msg.isError ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200 rounded-bl-none'}`}>
                            {msg.role === 'assistant' && !msg.isError && (
                              <div className="flex items-center space-x-2 mb-2">
                                <FaRobot className="h-3 w-3 text-purple-500" />
                                <span className="text-xs font-semibold text-purple-600">TripGenius AI</span>
                                {msg.fallback && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Basic</span>
                                )}
                              </div>
                            )}
                            <div className="text-sm" dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
                            <div className="text-xs opacity-70 mt-2">
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-none p-4 max-w-[85%]">
                            <div className="flex items-center space-x-3">
                              <FaSpinner className="h-4 w-4 text-purple-500 animate-spin" />
                              <span className="text-sm text-gray-600">Thinking about your trip...</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Quick Suggestions */}
                {quickSuggestions.length > 0 && messages.length <= 3 && (
                  <div className="px-4 py-3 border-t border-gray-200 bg-white">
                    <div className="flex items-center space-x-2 mb-3">
                      <FaLightbulb className="h-4 w-4 text-yellow-500" />
                      <span className="text-xs font-semibold text-gray-700">Quick Start</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {quickSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          onClick={() => handleQuickSuggestionClick(suggestion)}
                          disabled={isLoading}
                          className="text-xs bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 text-blue-700 border border-blue-200 px-3 py-2 rounded-xl transition-all hover:scale-105 active:scale-95"
                        >
                          {suggestion.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input Area */}
                <div className="border-t border-gray-200 p-4 bg-white">
                  <div className="flex space-x-3">
                    <div className="flex-1 relative">
                      <textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask about itinerary, budget, activities..."
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none bg-gray-50"
                        rows="1"
                        disabled={isLoading}
                      />
                      <button
                        onClick={() => sendMessage()}
                        disabled={isLoading || !inputMessage.trim()}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all ${isLoading || !inputMessage.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg'}`}
                      >
                        <FaPaperPlane className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-gray-500">
                      {connectionStatus === 'connected' ? '✅ Connected' : 
                       connectionStatus === 'connecting' ? '🔄 Connecting...' : 
                       '⚠️ Limited mode'}
                    </span>
                    <button
                      onClick={clearChat}
                      className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Clear chat
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Minimized State */}
            {isMinimized && (
              <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setIsMinimized(false)}>
                <div className="flex items-center space-x-3">
                  <FaRobot className="h-5 w-5 text-purple-600 animate-pulse" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">TripGenius AI</p>
                    <p className="text-xs text-gray-500">Click to chat about your trip</p>
                  </div>
                </div>
                {messages.length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center animate-bounce">
                    {messages.filter(m => m.role === 'assistant' && !m.seen).length}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;